import { css } from 'aphrodite';
import * as React from 'react';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useParams } from 'react-router-dom';
import { useGetGoogleBusinessReview } from '../../../queries';
import { THEME } from '../../components-v2/Theme/theme';
import { AppBarNavigationItemPortalDestinationId } from '../../components/AppBar';
import { LevitateAppShell } from '../../components/AppShell';
import { FabContext } from '../../components/FabContext';
import { NavigationBreadcrumbsBar } from '../../components/NavigationBreadcrumbsBar';
import { Portal } from '../../components/Portal';
import { TestimonialImageEditor } from '../../components/TestimonialImageEditor';
import { TestimonialSource } from '../../components/TestimonialImageEditor/types';
import { styleSheet } from './styles';

interface TestimonialLocationState {
	testimonialSource: TestimonialSource;
	model?: any;
}

const TestimonialImageEditorContainer: React.FC = () => {
	const { id } = useParams<{ id?: string }>();
	const location = useLocation<TestimonialLocationState>();
	const state = location.state || { testimonialSource: null };
	const isMobile = useMediaQuery({ maxWidth: THEME.breakpoints.md });

	const {
		data: googleReview,
		isPending,
		error,
	} = useGetGoogleBusinessReview({
		reviewId: id || '',
		enabled: !!id,
		refetchOnWindowFocus: false,
	});

	const testimonialSource: TestimonialSource | null = React.useMemo(() => {
		if (state.testimonialSource) {
			return state.testimonialSource;
		}

		if (googleReview && id) {
			const activeGoogleReview = { ...googleReview, includeGeneratedReply: false };
			return {
				type: 'google_review',
				data: activeGoogleReview,
			};
		}

		return null;
	}, [state.testimonialSource, googleReview, id]);

	if (isPending && id) {
		return (
			<div className={css(styleSheet.container)}>
				<h2>Loading testimonial data...</h2>
			</div>
		);
	}

	if (error && id) {
		return (
			<div className={css(styleSheet.container)}>
				<h2>Error: Failed to load Google review</h2>
				<p>Please check the review ID and try again.</p>
			</div>
		);
	}

	if (!testimonialSource) {
		return (
			<div className={css(styleSheet.container)}>
				<h2>Error: No testimonial data provided</h2>
				<p>Please provide a review ID in the URL or navigate from a review.</p>
			</div>
		);
	}

	return (
		<div className={css(styleSheet.container)}>
			{!isMobile && (
				<Portal destination={AppBarNavigationItemPortalDestinationId}>
					<NavigationBreadcrumbsBar
						currentLocationName='Custom Testimonial'
						pathComponentNameProvider={pathComponent =>
							pathComponent === 'testimonial-image-editor' && id ? null : 'Custom Testimonial'
						}
					/>
				</Portal>
			)}
			<TestimonialImageEditor testimonial={testimonialSource} />
		</div>
	);
};

export const TestimonialImageEditorContainerWrapper: React.FC = () => {
	return (
		<LevitateAppShell>
			<TestimonialImageEditorContainer />
			<FabContext appearance={{ hidden: true }} />
		</LevitateAppShell>
	);
};

export { TestimonialImageEditorContainerWrapper as TestimonialImageEditorContainer };
